import React, { Fragment, useLayoutEffect, useRef, useState } from "react";

import { PeopleBlock as Value } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { Themed } from "../../styles/themes";
import { HorizontalScrollWrapper } from "../HorizontalScrollWrapper";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SpacerBlock } from "../SpacerBlock";
import { PersonBlock } from "./PersonBlock";

import styles from "./index.module.scss";

const headerComponents = {
    rich_text_stream: RichTextStreamBlock,
    spacer: SpacerBlock,
};

/**
 * Above this much centered space, stop floating the content down and pin it
 * here instead; the excess falls below for the revealed next block to fill.
 */
const MAX_TOP_GAP = 175;

export const PeopleBlock = ({ value }: { value: Value }) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const sectionRef = useRef<HTMLElement>(null);
    const revealTargetRef = useRef<HTMLElement | null>(null);

    // The block to reveal is this section's next sibling. Captured once;
    // StreamField DOM is static after hydration. Must be a layout effect — the
    // wrapper reads this ref in its passive mount effect, which fires after all
    // layout effects.
    useLayoutEffect(() => {
        const next = sectionRef.current?.nextElementSibling;
        revealTargetRef.current = next instanceof HTMLElement ? next : null;
    }, []);

    if (!value.people || value.people.length === 0) return null;
    const people = value.people;

    return (
        <Themed
            theme={value.theme}
            tag="section"
            className={styles.root}
            ref={sectionRef}
        >
            <HorizontalScrollWrapper
                onActiveIndexChange={setActiveIndex}
                revealTarget={revealTargetRef}
                spaceReservationTarget={sectionRef}
                maxTopGap={MAX_TOP_GAP}
                header={
                    value.header && value.header.length > 0 ? (
                        <StreamField
                            tagName={Fragment}
                            components={headerComponents}
                            value={value.header}
                        />
                    ) : (
                        <></>
                    )
                }
            >
                {(innerRef) => (
                    <ul className={styles.list} ref={innerRef}>
                        {people.map((person, index) => (
                            <li
                                key={index}
                                className={
                                    index === activeIndex
                                        ? styles.activeListItem
                                        : styles.inactiveListItem
                                }
                            >
                                <PersonBlock value={person} />
                            </li>
                        ))}
                    </ul>
                )}
            </HorizontalScrollWrapper>
        </Themed>
    );
};
